import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSmartModalComponent, NgxSmartModalService, ToastrService } from '@bssh/comp-lib';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ModalTexts } from '../modal-texts';
import { IEditSharedBclSettingsModalInput } from '../model/action-modal';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { IFormField } from '@app/run-planning/model/form-field';
import { FormValueObject } from '@app/run-planning/interface';
import { EMPTY, Observable } from 'rxjs';
import { RenderAnalysisVersionDefinitionResponse, RenderContext } from '@stratus/gss-ng-sdk';
import { GssApiService } from '@app/run-planning/services/gss-api-service';
import { catchError, debounceTime, filter, finalize, flatMap, map, pairwise, startWith } from 'rxjs/operators';
import { SharedSpinnerService } from '@app/run-planning/services/shared-spinner/shared-spinner.service';
import { RunSettingsState } from '@app/run-planning/run-settings/run-settings.state';
import { getDifference } from '@app/run-planning/services/helper.service';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-edit-shared-bcl-settings-modal',
  templateUrl: './edit-shared-bcl-settings-modal.component.html',
  styleUrls: ['./edit-shared-bcl-settings-modal.component.scss']
})
export class EditSharedBclSettingsModalComponent extends BaseModalComponent implements OnInit, AfterViewInit, OnDestroy {

  public modalType = 'EditSharedBclConvertSettingsModal';
  public modalTitle = ModalTexts.EDIT_SHARED_BCL_CONVERT_SETTINGS.MODAL_TITLE;
  public closeButtonText = ModalTexts.EDIT_SHARED_BCL_CONVERT_SETTINGS.CLOSE_BUTTON_TEXT;
  public confirmButtonText = ModalTexts.EDIT_SHARED_BCL_CONVERT_SETTINGS.CONFIRM_BUTTON_TEXT;

  configTitle: string;
  bclConvertSettingsFields: IFormField[];
  showHyperLink: boolean;
  formGroup: FormGroup;

  private bclConvertAvdId: string;
  private runSettings: RunSettingsState;
  private isRequeue: boolean;
  private changesSaved: boolean = false;
  
  save: (...args: any[]) => Observable<any>;

  get bclConvertSettingsValues() { return this.formGroup ? this.formGroup.get('bclConvertSettingsValues') : null; }
  
  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    private toastrService: ToastrService,
    private gssApiService: GssApiService,
    private sharedSpinnerService: SharedSpinnerService
  ) {
    super(ngxSmartModalService);
  }

  ngOnInit() {
    this.subs.sink = this.data.subscribe((modalInput: IEditSharedBclSettingsModalInput) => {
      if (modalInput) {
        this.sharedSpinnerService.startSpinner();
        this.configTitle = modalInput.configTitle;
        this.bclConvertAvdId = modalInput.bclConvertAvd.id;
        this.runSettings = modalInput.runSettings;
        this.bclConvertSettingsFields = modalInput.bclConvertSettingsFields;
        this.isRequeue = modalInput.isRequeue;
        this.showHyperLink = modalInput.showHyperLink;
        this.save = modalInput.saveFunction;

        this.buildFormGroup(modalInput.bclConvertSettingsValues);
        this.setupValueChangeHandler();
        this.sharedSpinnerService.stopSpinner();
      }
    });
  }

  private buildFormGroup(bclConvertSettingsValues: FormValueObject) {
    this.formGroup = new FormBuilder().group({
      bclConvertSettingsValues: new FormControl(bclConvertSettingsValues)
    });
  }

  private setupValueChangeHandler() {
    const initialValues = { ...this.bclConvertSettingsValues.value };
    this.subs.sink = this.bclConvertSettingsValues.valueChanges
      .pipe(
        debounceTime(250),
        this.getFirstEffectiveChangedFieldId(initialValues),
        filter(changedFieldId => {
          if (!changedFieldId) {
            return false;
          }
          const changedField = this.bclConvertSettingsFields.find(x => x.id === changedFieldId);
          return changedField && changedField.updateRenderOnChange && !changedField.hidden;
        }),
        flatMap((changedFieldId) => {
          this.sharedSpinnerService.startSpinner();
          return this.gssApiService.renderAnalysisVersionDefinitionForSharedBcl(
            RenderContext.FIELD_CHANGED,
            this.bclConvertAvdId,
            this.runSettings,
            this.bclConvertSettingsValues.value,
            this.bclConvertSettingsFields,
            changedFieldId
          ).pipe(
            catchError(err => {
              this.toastrService.error('Error rendering shared BCL Convert. Some settings may not be available.');
              this.sharedSpinnerService.stopSpinner();
              return EMPTY;
            })
          );
        })
      ).subscribe((response: RenderAnalysisVersionDefinitionResponse) => {
        this.bclConvertSettingsFields = response.analysisVersionDefinition.analysisSettings.fields;
        this.bclConvertSettingsValues.setValue(response.runAnalysisConfiguration.settings);
        this.sharedSpinnerService.stopSpinner();
      });
  }

  /**
   * This is basically a combination of the onlyChangedValues helper function
   * and the firstEffectiveChange logic from sample-analysis-setup's callRenderAvdApiAsNeeded.
   */
  private getFirstEffectiveChangedFieldId = (initialState?: {}) => (source$: Observable<any>) => {
    initialState = initialState || {};
    return source$.pipe(
      startWith(initialState),
      pairwise(),
      map(([oldState, newState]) => {
        const fieldChanges = getDifference(oldState, newState);
        return Object.keys(fieldChanges).find(k => !(
          oldState[k] === newState[k] ||
          isEqual(oldState[k], newState[k])
        ));
      })
    );
  };
  
  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  /**
   * To be called whenever the modal is closed.
   * @param modal modal instance emitted as $event from eventEmitter
   */
  close(modal: NgxSmartModalComponent) {
    this.confirm.emit({
      changesSaved: this.changesSaved,
      bclConvertSettingsFields: this.bclConvertSettingsFields,
      bclConvertSettingsValues: this.bclConvertSettingsValues.value
    });
    this.confirm.complete();
  }

  /**
   * Override BaseModalComponent's accept(). Called when the confirm
   * button on modal is clicked to proceed with the action.
   * @param modal modal instance emitted as $event from eventEmitter
   */
  accept(modal: NgxSmartModalComponent) {
    if (this.isRequeue) {
      // should not call save when it is a requeue
      // just mark changesSaved as true immediately so that run-summary can save the changes in state
      this.changesSaved = true;
      modal.close();
    } else {
      this.save(this.bclConvertSettingsValues.value).subscribe({
        next: () => {
          this.changesSaved = true;
          this.toastrService.success('Changes saved successfully.');
          modal.close();
        },
        error: (err) => {
          this.toastrService.error(err.message, 'Error saving changes.');
        }
      });
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
